import type { ElementoAgenda } from "./agenda";
import { aFechaISO, diaDe, inicioDeSemana, sumarDias } from "./fechas";
import type { FechaISO } from "./tipos";

/**
 * Calendario estilo Google Calendar (petición de Robert, 02/10/2026): mes, semana y día.
 * Funciones puras; la pantalla solo pinta lo que devuelven.
 */

export type VistaCalendario = "mes" | "semana" | "dia";

/** Semanas (lunes a domingo) que cubren el mes de `referencia`: 4 a 6 filas de 7 días. */
export const semanasDelMes = (referencia: Date): Date[][] => {
  const primero = new Date(referencia.getFullYear(), referencia.getMonth(), 1);
  const ultimo = new Date(
    referencia.getFullYear(),
    referencia.getMonth() + 1,
    0,
  );
  const semanas: Date[][] = [];
  for (
    let lunes = inicioDeSemana(primero);
    lunes <= ultimo;
    lunes = sumarDias(lunes, 7)
  ) {
    semanas.push(Array.from({ length: 7 }, (_, i) => sumarDias(lunes, i)));
  }
  return semanas;
};

/** Los 7 días (lunes a domingo) de la semana de `referencia`. */
export const diasDeLaSemana = (referencia: Date): Date[] => {
  const lunes = inicioDeSemana(referencia);
  return Array.from({ length: 7 }, (_, i) => sumarDias(lunes, i));
};

const ORDEN_TIPO: Record<ElementoAgenda["tipo"], number> = {
  reunion: 0,
  presentacion: 0,
  siguiente_paso: 1,
  vuelta: 2,
};

/**
 * Elementos agrupados por día ("YYYY-MM-DD"). Dentro de cada día: primero las reuniones
 * (consultorías y presentaciones) por hora, después los siguientes pasos y las vueltas de Largo plazo, por nombre.
 */
export const agruparPorDia = (
  elementos: ElementoAgenda[],
): Map<FechaISO, ElementoAgenda[]> => {
  const dias = new Map<FechaISO, ElementoAgenda[]>();
  for (const e of elementos) {
    const clave = aFechaISO(diaDe(e.fecha));
    const lista = dias.get(clave) ?? [];
    lista.push(e);
    dias.set(clave, lista);
  }
  for (const lista of dias.values()) {
    lista.sort(
      (a, b) =>
        ORDEN_TIPO[a.tipo] - ORDEN_TIPO[b.tipo] ||
        (a.conHora && b.conHora ? a.fecha.localeCompare(b.fecha) : 0) ||
        a.negocio.nombre.localeCompare(b.negocio.nombre, "es"),
    );
  }
  return dias;
};

/** Mueve la fecha de referencia una unidad de la vista (mes, semana o día). */
export const desplazar = (
  referencia: Date,
  vista: VistaCalendario,
  pasos: number,
): Date => {
  if (vista === "mes") {
    return new Date(referencia.getFullYear(), referencia.getMonth() + pasos, 1);
  }
  return sumarDias(referencia, vista === "semana" ? 7 * pasos : pasos);
};

/**
 * Colores por responsable: oscuros para que el texto blanco (inicial) pase AA (≥ 5:1).
 * Se asignan por orden de id de usuario, así cada persona tiene siempre el mismo color.
 */
export const COLORES_RESPONSABLE = [
  "#336394",
  "#1F7A4D",
  "#7A3E9D",
  "#A14A1A",
  "#2B6F75",
  "#9B2C5A",
  "#5B5BD6",
  "#8A6A00",
] as const;
export const COLOR_SIN_ASIGNAR = "#6B7280";

export const colorDeResponsable = (
  responsable: string | number | null | undefined,
  idsUsuarios: (string | number)[],
): string => {
  if (responsable === null || responsable === undefined)
    return COLOR_SIN_ASIGNAR;
  const ordenados = [...idsUsuarios]
    .map(String)
    .sort((a, b) => Number(a) - Number(b));
  const i = ordenados.indexOf(String(responsable));
  return i < 0
    ? COLOR_SIN_ASIGNAR
    : COLORES_RESPONSABLE[i % COLORES_RESPONSABLE.length];
};

/**
 * Enlace «Añadir a Google Calendar» (Robert, 14/10/2026): abre Google Calendar con la cita
 * rellenada, para guardarla con un clic en el calendario de quien lo pulsa. Sin conexión ni
 * permisos: el CRM no guarda nada en Google.
 * - `dia` (AAAA-MM-DD): cita de día entero.
 * - `inicio` (instante ISO): cita con hora, de `minutos` de duración (30 por defecto).
 */
export const enlaceGoogleCalendar = ({
  titulo,
  dia,
  inicio,
  minutos = 30,
  detalles,
}: {
  titulo: string;
  dia?: string | null;
  inicio?: string | null;
  minutos?: number;
  detalles?: string;
}): string | null => {
  let fechas: string;
  if (inicio) {
    const desde = new Date(inicio);
    if (Number.isNaN(desde.getTime())) return null;
    const hasta = new Date(desde.getTime() + minutos * 60_000);
    const utc = (d: Date) =>
      d
        .toISOString()
        .replace(/[-:]/g, "")
        .replace(/\.\d{3}Z$/, "Z");
    fechas = `${utc(desde)}/${utc(hasta)}`;
  } else if (dia && /^\d{4}-\d{2}-\d{2}$/.test(dia)) {
    const [a, m, d] = dia.split("-").map(Number);
    const siguiente = new Date(Date.UTC(a, m - 1, d + 1));
    const compacta = (x: string) => x.replace(/-/g, "");
    fechas = `${compacta(dia)}/${compacta(siguiente.toISOString().slice(0, 10))}`;
  } else {
    return null;
  }
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: titulo,
    dates: fechas,
    ctz: "Europe/Madrid",
  });
  if (detalles) params.set("details", detalles);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};
